import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faSpinner } from '@fortawesome/free-solid-svg-icons';
import DataTable from '../common/DataTable';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';


const PurchaseDaybookTable = ({
    daybookEntries = [],
    loading = false,
    selectedEntries = [],
    onSelectEntry,
    onSelectAll,
    // When provided, an actions column with a per-row Print button is shown
    // (used by the Purchase Estimate page). Omit it and the column is hidden
    // (Purchase Daybook page keeps its original columns-only layout).
    onPrintRow,
    printingRowId,
}) => {
    const { t } = useTranslation();

    const selectionColumn = useMemo(() => ({
        id: 'selection',
        sortable: false,
        width: '40px',
        renderHeader: () => {
            const currentPageIds = daybookEntries.map(e => String(e.id));
            const isAllSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedEntries.includes(id));
            return (
                <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    checked={isAllSelected}
                    onChange={(e) => onSelectAll?.(e.target.checked, currentPageIds)} 
                />
            );
        },
        renderCell: (e) => {
            const id = String(e.id);
            return (
                <div onClick={(evt) => evt.stopPropagation()}>
                    <input 
                        type="checkbox" 
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        checked={selectedEntries.includes(id)}
                        onChange={(evt) => onSelectEntry?.(id, evt.target.checked)} 
                    />
                </div>
            );
        },
        skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
    }), [daybookEntries, selectedEntries, onSelectAll, onSelectEntry]);

    const baseColumns = useMemo(() => [
        selectionColumn,
        {
            id: 'date',
            label: t('purchaseDaybook.table.headers.date'),
            sortable: true,
            sortAccessor: (r) => r.date || '',
            renderCell: (r) => <span className="text-sm">{r.date || t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'serial_number',
            label: t('purchaseDaybook.table.headers.serialNumber'),
            sortable: true,
            sortAccessor: (r) => (r.serial_number || '').toLowerCase(),
            renderCell: (r) => <span className="text-sm text-gray-500">{r.serial_number || t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-12 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'po_number',
            label: t('purchaseDaybook.table.headers.poNumber'),
            sortable: true,
            sortAccessor: (r) => (r.po_number || '').toLowerCase(),
            renderCell: (r) => <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{r.po_number || t('common.symbols.emDash')}</div>,
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'tally_name',
            label: t('purchaseDaybook.table.headers.tallyName'),
            sortable: true,
            sortAccessor: (r) => (r.tally_name || '').toLowerCase(),
            renderCell: (r) => <span className="text-sm">{r.tally_name || t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-36 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'vehicle_numbers',
            label: t('purchaseDaybook.table.headers.vehicleNumber'),
            sortable: true,
            sortAccessor: (r) => (r.vehicle_numbers || '').toLowerCase(),
            renderCell: (r) => <span className="text-sm">{r.vehicle_numbers || t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-28 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'actual_quantity',
            label: "Total Weight",
            sortable: true,
            sortAccessor: (r) => (r.actual_quantity ? parseFloat(r.actual_quantity) : 0),
            renderCell: (r) => <span className="text-sm">{r.actual_quantity != null ? r.actual_quantity : t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
            align: 'right',
        },
        {
            id: 'total_amount',
            label: t('purchaseDaybook.table.headers.totalAmount'),
            sortable: true,
            sortAccessor: (r) => (r.total_amount ? parseFloat(r.total_amount) : 0),
            renderCell: (r) => <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{r.total_amount != null ? `${t('common.symbols.currency')}${Number(r.total_amount).toFixed(2)}` : t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
            align: 'right',
        },
        {
            id: 'remark',
            label: t('purchaseDaybook.table.headers.remark'),
            sortable: false,
            renderCell: (r) => {
                if (!r.remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.symbols.emDash')}</span>;
                return (
                    <div 
                        className="text-[11px] font-medium break-words leading-relaxed min-w-[200px] px-2 py-1.5 rounded border" 
                        title={r.remark} 
                        style={{ 
                            backgroundColor: 'rgba(254, 243, 199, 0.5)', 
                            color: '#92400e', 
                            borderColor: 'rgba(217, 119, 6, 0.2)',
                            whiteSpace: 'pre-wrap'
                        }}
                    >
                        {r.remark}
                    </div>
                );
            },
            skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
        },
        ...(onPrintRow ? [{
            id: 'actions',
            label: t('common.table.actions'),
            sortable: false,
            renderCell: (r) => {
                const id = r.po_id || r.id || r.po_number;
                const isPrinting = printingRowId != null && String(printingRowId) === String(id);
                return (
                    <div className="flex items-center justify-center space-x-2">
                        <button
                            onClick={() => onPrintRow(r)}
                            disabled={isPrinting}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: '#f3f4f6' }}
                            title={t('common.actions.print')}
                        >
                            <FontAwesomeIcon icon={isPrinting ? faSpinner : faPrint} spin={isPrinting} className="text-sm" />
                        </button>
                    </div>
                );
            },
            skeleton: <div className="w-8 h-8 bg-gray-300 rounded-lg" />,
        }] : []),
    ], [selectionColumn, onPrintRow, printingRowId, t]);

    const emptyState = useMemo(() => ({
        message: t('purchaseDaybook.table.empty.message'),
        title: t('purchaseDaybook.table.empty.title'),
    }), [t]);

    return (
        <div className="bg-white mt-2 rounded-lg shadow-sm border border-gray-200">
            <DataTable
                data={Array.isArray(daybookEntries) ? daybookEntries : []}
                columns={baseColumns}
                loading={loading}
                keyField="id"
                enablePagination={false}
                defaultSort={{ field: 'serial_number', direction: 'asc' }}
                emptyState={emptyState}
            />
        </div>
    );
};

export default PurchaseDaybookTable;
